import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_URL_BACKEND || 'http://localhost:3001';

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // NÃO redirecionar para login se for erro do weather (API externa)
      const isWeatherEndpoint = error.config?.url?.includes('/weather');
      
      if (!isWeatherEndpoint) {
        // Token expirado ou inválido (apenas para endpoints internos)
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================
// AUTH SERVICES
// ============================

export const authService = {
  login: async (email, password) => {
    const response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
      email,
      password,
    });
    return response.data;
  },

  register: async (username, email, password) => {
    const response = await api.post('/api/auth/register', {
      username,
      email,
      password,
    });
    return response.data;
  },
};

// ============================
// SITES SERVICES
// ============================

export const sitesService = {
  getAll: async (empresaClienteId) => {
    const params = empresaClienteId ? { empresaClienteId } : {};
    const response = await api.get('/api/sites', { params });
    return response.data;
  },

  getAllWithRealtime: async (empresaClienteId) => {
    const params = empresaClienteId ? { empresaClienteId } : {};
    const response = await api.get('/api/sites/realtime/all', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/sites/${id}`);
    return response.data;
  },

  getWithRealtime: async (id) => {
    const response = await api.get(`/api/sites/${id}/realtime`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/sites/stats');
    return response.data;
  },

  create: async (siteData) => {
    const response = await api.post('/api/sites', siteData);
    return response.data;
  },

  createComplete: async (completeSiteData) => {
    const response = await api.post('/api/sites/complete', completeSiteData);
    return response.data;
  },

  update: async (id, siteData) => {
    const response = await api.put(`/api/sites/${id}`, siteData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/sites/${id}`);
    return response.data;
  },

  getReadings: async (id, period = 'day') => {
    const response = await api.get(`/api/sites/${id}/readings`, { params: { period } });
    return response.data;
  },

  updateCoordinates: async (id, latitude, longitude) => {
    const response = await api.put(`/api/sites/${id}/coordinates`, { latitude, longitude });
    return response.data;
  },

  forceFixQuahary: async () => {
    const response = await api.post('/api/sites/fix-quahary-coordinates');
    return response.data;
  },

  debugQuahary: async () => {
    const response = await api.get('/api/sites/debug/quahary');
    return response.data;
  },

  fixQuaharyCity: async () => {
    const response = await api.post('/api/sites/fix-quahary-city');
    return response.data;
  },
};

// ============================
// EMPRESA CLIENTES
// ============================

export const empresaClientesService = {
  getAll: async () => {
    const response = await api.get('/api/empresa-clientes');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/api/empresa-clientes/${id}`);
    return response.data;
  },
};

// ============================
// ANEEL SERVICES
// ============================

export const aneelService = {
  getCombinedData: async (params = {}) => {
    const response = await api.get('/api/aneel/combined-data', { params });
    return response.data;
  },
};

// ============================
// EQUIPAMENTOS SERVICES
// ============================

export const equipamentosService = {
  getAll: async (filters = {}) => {
    const response = await api.get('/api/equipamentos', { params: filters });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/api/equipamentos/${id}`);
    return response.data;
  },

  getByType: async (type) => {
    const response = await api.get(`/api/equipamentos/type/${type}`);
    return response.data;
  },

  getBySite: async (siteId) => {
    const response = await api.get(`/api/equipamentos/site/${siteId}`);
    return response.data;
  },

  // Buscar equipamentos de plantas reais via API Goodwe
  getDevicesFromGoodwe: async (usinaId) => {
    const response = await api.get(`/api/usinas/${usinaId}/devices`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/equipamentos/stats');
    return response.data;
  },

  create: async (equipmentData) => {
    const response = await api.post('/api/equipamentos', equipmentData);
    return response.data;
  },

  update: async (id, equipmentData) => {
    const response = await api.put(`/api/equipamentos/${id}`, equipmentData);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/api/equipamentos/${id}`);
    return response.data;
  },
};

// ============================
// GOODWE API SERVICES
// ============================

export const goodweService = {
  getPlants: async (page, perPage) => {
    const params = {};
    if (page) params.page = page;
    if (perPage) params.perPage = perPage;
    const response = await api.get('/api/goodwe/plants', { params });
    return response.data;
  },

  getPlantDetail: async (id) => {
    const response = await api.get(`/api/goodwe/plants/${id}`);
    return response.data;
  },

  getPlantPower: async (id) => {
    const response = await api.get(`/api/goodwe/plants/${id}/power`);
    return response.data;
  },

  clearCache: async () => {
    const response = await api.post('/api/goodwe/cache/clear');
    return response.data;
  },
};

// ============================
// HEALTH CHECK
// ============================

export const healthService = {
  check: async () => {
    const response = await api.get('/api/health');
    return response.data;
  },

  ready: async () => {
    const response = await api.get('/api/health/ready');
    return response.data;
  },
};

// ============================
// ANALYTICS SERVICES
// ============================

export const analyticsService = {
  getOverview: async (period = 'today', siteId = null) => {
    const params = { period };
    if (siteId) params.siteId = siteId;
    const response = await api.get('/api/analytics/overview', { params });
    return response.data;
  },

  getChargingStations: async (period = 'today', siteId = null) => {
    const params = { period };
    if (siteId) params.siteId = siteId;
    const response = await api.get('/api/analytics/charging-stations', { params });
    return response.data;
  },

  getEnergyFlow: async (period = 'today', siteId = null) => {
    const params = { period };
    if (siteId) params.siteId = siteId;
    const response = await api.get('/api/analytics/energy-flow', { params });
    return response.data;
  },

  getEnergyFlowRealtime: async (siteId = null) => {
    const params = {};
    if (siteId) params.siteId = siteId;
    const response = await api.get('/api/analytics/energy-flow-realtime', { params });
    return response.data;
  },

  getEvChargerLatest: async (siteId = null) => {
    const params = {};
    if (siteId) params.siteId = siteId;
    const response = await api.get('/api/analytics/ev-charger-latest', { params });
    return response.data;
  },

  getEvSessions: async (siteId = null, startDate = null, endDate = null) => {
    const params = {};
    if (siteId)    params.siteId    = siteId;
    if (startDate) params.startDate = startDate;
    if (endDate)   params.endDate   = endDate;
    const response = await api.get('/api/analytics/ev-sessions', { params });
    return response.data;
  },
};

// ============================
// WEATHER SERVICES
// ============================

export const weatherService = {
  getCurrentWeather: async (lat, lon, usinaId = null, siteName = null) => {
    const params = {};
    if (usinaId) params.usinaId = usinaId;
    if (siteName) params.siteName = siteName;
    const response = await api.get(`/api/weather/current/${lat}/${lon}`, { params });
    return response.data;
  },

  getForecast: async (lat, lon) => {
    const response = await api.get(`/api/weather/forecast/${lat}/${lon}`);
    return response.data;
  },
};

// ============================
// CCEE SERVICES
// ============================

export const cceeService = {
  getPrecoMedio: async () => {
    const response = await api.get('/api/ccee/preco-medio');
    return response.data;
  },

  getGeracaoPorFonte: async () => {
    const response = await api.get('/api/ccee/geracao-fonte');
    return response.data;
  },

  getResumoMercado: async () => {
    const response = await api.get('/api/ccee/resumo-mercado');
    return response.data;
  },

  getPldDetalhado: async () => {
    const response = await api.get('/api/ccee/pld-detalhado');
    return response.data;
  },
};

// ============================
// AI / CHAT SERVICE
// ============================

export const aiService = {
  chat: async (message, history = [], empresaClienteId = null) => {
    const body = { message, history };
    if (empresaClienteId) body.empresaClienteId = empresaClienteId;
    const response = await api.post('/api/ai/chat', body);
    return response.data;
  },
};

// ============================
// USUARIOS SERVICE
// ============================

export const usuariosService = {
  getAll: async () => {
    const response = await api.get('/api/usuarios');
    return response.data;
  },
  getEmpresas: async () => {
    const response = await api.get('/api/usuarios/empresas');
    return response.data;
  },
  create: async (dto) => {
    const response = await api.post('/api/usuarios', dto);
    return response.data;
  },
  updateRole: async (id, role) => {
    const response = await api.patch(`/api/usuarios/${id}/role`, { role });
    return response.data;
  },
  updateStatus: async (id, status) => {
    const response = await api.patch(`/api/usuarios/${id}/status`, { status });
    return response.data;
  },
  resetPassword: async (id, password) => {
    const response = await api.patch(`/api/usuarios/${id}/password`, { password });
    return response.data;
  },
  associate: async (email, empresaClienteId) => {
    const response = await api.post('/api/usuarios/associate', { email, empresaClienteId });
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/api/usuarios/${id}`);
    return response.data;
  },
};

// Exportar instância do axios para usos customizados
export default api;
